import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Paper,
    Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { AuthService } from '../../../services/auth.service';
import { RepairBillService } from '../../../services/maintenance.service';
import { SupplierBillService } from '../../../services/supplierBill.service';
import { SupplierBill } from '../../../interfaces/procurement/supplierBill.interface';
import SearchableSelect from '../../common/SearchableSelect';
import { RepairBillDTO, RepairBillFile } from '../../../interfaces/maintenance';
import { useToast } from '../../../context/ToastContext';
import { useDashboardAccess } from '../../dashboard/useDashboardAccess';
import ScrollableTable from '../../common/ScrollableTable';
import { formatMoney } from '../../../utils/format';
import { formatDate } from '../../../utils/dateUtils';
import { saveBlob } from '../../../utils/saveBlob';
import FocusedRowChip from '../../common/FocusedRowChip';
import { useFocusedRow } from '../../../hooks/useFocusedRow';
import { REPAIR_BILL_COLOR, repairBillLabel } from '../../maintenance/RepairBillsPanel';

/** Accounts queue of repair bills that maintenance sent for approval. Approving opens the service bill form pre-filled. */
export const RepairBillQueue: React.FC = () => {
    const institutionId = AuthService.getSelectedInstitution() as string;
    const toast = useToast();
    const navigate = useNavigate();
    const { enabledModules } = useDashboardAccess();
    const accountingOn = enabledModules.has('accounting');
    const [rows, setRows] = useState<RepairBillDTO[]>([]);
    const [files, setFiles] = useState<Record<string, RepairBillFile[]>>({});
    const [loading, setLoading] = useState(true);
    const [rejecting, setRejecting] = useState<RepairBillDTO | null>(null);
    const [reason, setReason] = useState('');
    const [saving, setSaving] = useState(false);
    const [linking, setLinking] = useState<RepairBillDTO | null>(null);
    const [linkBills, setLinkBills] = useState<SupplierBill[]>([]);
    const [linkBillId, setLinkBillId] = useState('');
    const { focusId, clear: clearFocus } = useFocusedRow();
    const shown = focusId ? rows.filter(b => b.id === focusId) : rows;

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const list = await RepairBillService.queue(institutionId);
            setRows(list);
            const entries = await Promise.all(list.map(async b => [b.id, await RepairBillService.listFiles(b.id)] as const));
            setFiles(Object.fromEntries(entries));
        } catch (e) {
            toast.showApiError(e, 'Failed to load repair bills');
        } finally {
            setLoading(false);
        }
    }, [institutionId]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { load(); }, [load]);

    const download = async (billId: string, f: RepairBillFile) => {
        try {
            const { blob, fileName } = await RepairBillService.downloadFile(billId, f.id);
            saveBlob(blob, fileName || f.fileName);
        } catch (e) {
            toast.showApiError(e, 'Failed to download the file');
        }
    };

    // Posted service bills only (draft and reversed ones are refused by the server too).
    const openLink = async (b: RepairBillDTO) => {
        setLinking(b);
        setLinkBillId('');
        setLinkBills([]);
        try {
            const page = b.supplierId
                ? await SupplierBillService.listBySupplier(b.supplierId, 0, 100)
                : await SupplierBillService.listByInstitution(0, 100);
            setLinkBills(page.content.filter(x => !x.purchaseOrderId && !x.openingBalance
                && ['UNPAID', 'PARTIALLY_PAID', 'PAID', 'BLOCKED'].includes(x.status)));
        } catch (e) {
            toast.showApiError(e, 'Failed to load the supplier bills');
        }
    };

    const link = async () => {
        if (!linking || !linkBillId) return;
        try {
            setSaving(true);
            const done = await RepairBillService.linkExisting(linking.id, linkBillId);
            toast.showSuccess('Repair bill approved and linked to the supplier bill');
            if (done.warning) toast.showWarning(done.warning);
            setLinking(null);
            await load();
        } catch (e) {
            toast.showApiError(e, 'Failed to link the supplier bill');
        } finally {
            setSaving(false);
        }
    };

    const reject = async () => {
        if (!rejecting) return;
        try {
            setSaving(true);
            await RepairBillService.reject(rejecting.id, reason.trim());
            toast.showSuccess('Repair bill rejected');
            setRejecting(null);
            setReason('');
            await load();
        } catch (e) {
            toast.showApiError(e, 'Failed to reject the repair bill');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }} data-help-page="procurement.repairBills.page">
            <Typography variant="h5">Repair bills waiting</Typography>
            <FocusedRowChip focusId={focusId} noun="repair bill" onClear={clearFocus} />
            <Alert severity="info">
                Maintenance sent these vendor bills for repairs. Approve opens a service bill with the details filled in; posting it
                approves the repair bill. Reject sends it back to maintenance with your reason. Nothing is added to the asset cost.
            </Alert>
            {!accountingOn && (
                <Alert severity="warning" data-testid="accounting-off">
                    Accounting is not switched on for this company, so a repair bill cannot be approved yet. You can still read and reject them.
                </Alert>
            )}
            {loading ? <CircularProgress /> : (
                <Paper sx={{ p: 1 }} data-help-section="procurement.repairBills.section.list">
                    <ScrollableTable>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Work order</TableCell>
                                    <TableCell>Equipment / asset</TableCell>
                                    <TableCell>Vendor</TableCell>
                                    <TableCell align="right">Amount</TableCell>
                                    <TableCell>Bill</TableCell>
                                    <TableCell>Sent by</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {shown.length === 0 && (
                                    <TableRow><TableCell colSpan={8}>{focusId ? 'This repair bill is no longer waiting: it was already decided.' : 'No repair bills are waiting.'}</TableCell></TableRow>
                                )}
                                {shown.map(b => (
                                    <TableRow key={b.id}>
                                        <TableCell>{b.woNo}{b.woTitle ? ` - ${b.woTitle}` : ''}</TableCell>
                                        <TableCell>
                                            {b.equipmentCode ? `${b.equipmentCode} ${b.equipmentName ?? ''}` : '-'}
                                            {b.fixedAssetCode && <Typography variant="caption" display="block">Asset {b.fixedAssetCode} {b.fixedAssetName ?? ''}</Typography>}
                                        </TableCell>
                                        <TableCell>{b.supplierName ?? b.vendorName ?? '-'}</TableCell>
                                        <TableCell align="right">{formatMoney(b.amount)}</TableCell>
                                        <TableCell>
                                            {b.billRef ?? '-'}{b.billDate ? ` (${formatDate(b.billDate)})` : ''}
                                            {(files[b.id] ?? []).map(f => (
                                                <Button key={f.id} size="small" onClick={() => download(b.id, f)}>{f.fileName}</Button>
                                            ))}
                                        </TableCell>
                                        <TableCell>{b.submittedByName ?? '-'}</TableCell>
                                        <TableCell><Chip size="small" color={REPAIR_BILL_COLOR[b.status]} label={repairBillLabel(b.status)} /></TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                                <Button data-help="procurement.repairBills.approve" size="small" variant="contained" disabled={!accountingOn}
                                                    onClick={() => navigate(`/procurement/supplier-bills/new-service?repairBill=${b.id}`)}>Approve</Button>
                                                <Button data-help="procurement.repairBills.link" size="small" disabled={!accountingOn}
                                                    onClick={() => openLink(b)}>Link existing bill</Button>
                                                <Button data-help="procurement.repairBills.reject" size="small" color="error"
                                                    onClick={() => { setRejecting(b); setReason(''); }}>Reject</Button>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </ScrollableTable>
                </Paper>
            )}
            <Dialog open={linking !== null} onClose={() => setLinking(null)} maxWidth="sm" fullWidth>
                <DialogTitle>Link a posted bill to {linking?.woNo}</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                    <Typography variant="body2">
                        Use this when the vendor bill was already posted on the normal service bill form. Pick it here instead of posting it twice.
                        Repair bill amount: {linking ? formatMoney(linking.amount) : ''}.
                    </Typography>
                    <SearchableSelect<SupplierBill>
                        data-help="procurement.repairBills.field.linkBill"
                        label="Supplier bill" value={linkBillId} options={linkBills} size="small"
                        getOptionLabel={x => `${x.billNumber}${x.supplierName ? ` - ${x.supplierName}` : ''} - ${formatMoney(x.totalAmount)} (${x.status})`}
                        onChange={id => setLinkBillId(id)} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setLinking(null)}>Cancel</Button>
                    <Button variant="contained" disabled={!linkBillId || saving} onClick={link}>Link and approve</Button>
                </DialogActions>
            </Dialog>
            <Dialog open={rejecting !== null} onClose={() => setRejecting(null)} maxWidth="sm" fullWidth>
                <DialogTitle>Reject repair bill {rejecting?.woNo}</DialogTitle>
                <DialogContent>
                    <TextField data-help="procurement.repairBills.field.reason" autoFocus margin="dense" label="Reason" required multiline minRows={2}
                        value={reason} onChange={e => setReason(e.target.value)} sx={{ flex: '1 1 300px' }} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRejecting(null)}>Cancel</Button>
                    <Button color="error" variant="contained" disabled={reason.trim() === '' || saving} onClick={reject}>Reject</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default RepairBillQueue;
